import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { AdminStudentPlatformHeaderComponent } from '../../admin-student-platform/admin-student-platform-header/admin-student-platform-header.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { CompanyService } from '@edtech-ui/core/services/company-management/company.service';
import { GetAllCompany } from '@edtech-ui/core/models/company-management/get-all-companies-list';
import { finalize, Subscription, tap, Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { CompanyApprovalPayload } from '@edtech-ui/core/models/company-management/accept-reject-company-approval';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';


@Component({
	selector: 'app-admin-all-companies',
	standalone: true,
	imports: [CommonModule, FormsModule, AdminStudentPlatformHeaderComponent, MatTooltipModule, MatProgressSpinnerModule],
	templateUrl: './admin-all-companies.component.html',
	styleUrl: './admin-all-companies.component.scss'
})
export class AdminAllCompaniesComponent implements OnInit, OnDestroy {

	searchTerm = '';
	selectedStatus = 'All Status';
	selectedIndustry = '';
	isLoading = false;
	showOnboardModal = false;
	showDetailedModal = false;
	showActionMenuId: string | null = null;
	industries: string[] = [];
	companiesData: GetAllCompany[] = [];
	allCompaniesData: any[] = [];
	selectedCompany: GetAllCompany | null = null;
	showApproveModal = false;
	showRejectModal = false;
	isApprovalLoading: boolean = false;
	rejectionReason = '';
	total = 0;
	page = 1;
	limit = 10;
	totalPages = 1;
	status = 'approved';
	sortField = '';
	sortDirection: 'asc' | 'desc' = 'asc';
	subscription = new Subscription();
	searchSubscription = new Subscription();
	searchSubject = new Subject<string>();
	showingFrom = 0;
	showingTo = 0;
	selectedDate = '';


	constructor(
		private toastr: ToastrService,
		private router: Router,
		private companyService: CompanyService,
		private route: ActivatedRoute
	) { }

	ngOnInit(): void {
		const initialPage = Number(this.route.snapshot.queryParams['page']);
		if (initialPage && !isNaN(initialPage) && initialPage > 0) {
			this.page = initialPage;
		}
		this.searchSubscription.add(
			this.searchSubject.pipe(debounceTime(500), distinctUntilChanged()).subscribe(() => {
				this.page = 1;
				this.getAllCompany();
			})
		);
		this.loadIndustrySuggestions();
		this.getAllCompany();

		this.subscription.add(
			this.route.queryParams.subscribe(params => {
				const queryPage = Number(params['page']);
				if (queryPage && !isNaN(queryPage) && queryPage > 0 && queryPage !== this.page) {
					this.page = queryPage;
					this.getAllCompany();
				}
			})
		);
	}

	loadIndustrySuggestions(): void {
		this.subscription.add(
			this.companyService.getAllCompaniesList({ limit: 100 }).subscribe({
				next: (response) => {
					if (response.Success && response.Data?.companies) {
						this.industries = Array.from(
							new Set(response.Data.companies.map((c: any) => c.industry).filter(Boolean))
						).sort() as string[];
					}
				}
			})
		);
	}

	onStatusChange(): void {

		if (this.selectedStatus === 'All Status') {
			this.status = '';
		}
		else if (this.selectedStatus === 'Active') {
			this.status = 'approved';
		}
		else if (this.selectedStatus === 'Pending') {
			this.status = 'pending';
		}
		else if (this.selectedStatus === 'Blocked') {
			this.status = 'blocked';
		}

		this.page = 1;
		this.getAllCompany();
	}


	onSearch(): void {
		this.searchSubject.next(this.searchTerm?.trim() || '');
	}


	exportToExcel(): void {

		const exportData = this.companiesData.map(company => ({
			'Company Name': company.company_name,
			'Industry': company.industry,
			'Contact Person': company.poc_name,
			'Contact Email': company.poc_email,
			'Official Email': company.official_email,
			'Mobile': company.poc_phone,
			'Status': company.status,
			'Created Date': company.created_at
		}));

		const worksheet: XLSX.WorkSheet =
			XLSX.utils.json_to_sheet(exportData);

		const workbook: XLSX.WorkBook = {
			Sheets: { 'Companies': worksheet },
			SheetNames: ['Companies']
		};

		const excelBuffer = XLSX.write(workbook, {
			bookType: 'xlsx',
			type: 'array'
		});

		const blob = new Blob(
			[excelBuffer],
			{
				type:
					'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
			}
		);

		saveAs(blob, `companies_${new Date().toISOString().slice(0, 10)}.xlsx`);
		this.toastr.success('Excel exported successfully');
	}


	prevPage(): void {
		if (this.page > 1) {
			this.page--;
			this.getAllCompany();
		}
	}

	nextPage(): void {
		if (this.page < this.totalPages) {
			this.page++;
			this.getAllCompany();
		}
	}

	// Close action menu on click outside
	closeMenus(): void {
		this.showActionMenuId = null;
	}


	getAllCompany(): void {
		this.isLoading = true;
		const payload = {
			status: this.status || undefined,
			search: this.searchTerm?.trim() || undefined,
			created_at: this.selectedDate || undefined,
			industry: this.selectedIndustry || undefined,
			page: this.page,
			limit: this.limit
		};

		this.subscription.add(
			this.companyService.getAllCompaniesList(payload).pipe(
				finalize(() => {
					this.isLoading = false;
				})
			).subscribe({
				next: (response) => {
					if (response.Success) {
						this.total = response.Data.total;
						this.page = response.Data.page;
						this.limit = response.Data.limit;
						this.totalPages = response.Data.total_pages;
						this.companiesData = response?.Data?.companies || [];
						this.showingFrom = this.total === 0 ? 0 : ((this.page - 1) * this.limit) + 1;
						this.showingTo = Math.min(this.page * this.limit, this.total);
					} else {
						this.toastr.error(response.Message);
					}
				},

				error: () => {
					this.toastr.error('Failed to fetch companies');
				}
			})
		);
	}


	onDateChange(): void {
		this.page = 1;
		this.getAllCompany();
	}

	clearDateFilter(): void {
		this.selectedDate = '';
		this.page = 1;
		this.getAllCompany();
	}

	onIndustryChange(): void {
		this.page = 1;
		this.getAllCompany();
	}

	resetFilters(): void {
		this.searchTerm = '';
		this.selectedDate = '';
		this.selectedIndustry = '';
		this.page = 1;
		this.getAllCompany();
	}


	openOnboardModal(): void {
		this.showOnboardModal = true;
	}


	toggleActionMenu(event: Event, companyId: string): void {
		event.stopPropagation();

		this.showActionMenuId =
			this.showActionMenuId === companyId ? null : companyId;
	}


	postAcceptRejectCompanyApproval(action: 'approve' | 'reject' | 'block') {

		if (!this.selectedCompany?.company_id) {
			return;
		}

		const payload: CompanyApprovalPayload = {
			action: action,

			notes:
				action === 'approve'
					? 'Company approved successfully'
					: action === 'reject'
						? 'Company rejected successfully'
						: 'Company blocked successfully',

			rejection_reason:
				action === 'reject' ? this.rejectionReason : null
		};

		this.isApprovalLoading = true;
		this.subscription.add(
			this.companyService.postAcceptRejectCompanyApproval(payload, this.selectedCompany.company_id,).pipe(
				tap((response) => {
					if (response.Success) {
						this.toastr.success(response.Data.message);
						this.showActionMenuId = null;
						this.getAllCompany();
					}
				})
			).subscribe({
				error: (error) => {
					this.toastr.error(error?.error?.Message || 'Something went wrong');
				},
				complete: () => {
					this.isApprovalLoading = false;
				}
			})
		);
	}


	updateCompanyStatus(company: GetAllCompany, action: 'approve' | 'reject' | 'block'): void {
		this.selectedCompany = company;
		if (action === 'approve') {
			this.postAcceptRejectCompanyApproval('approve');
			return;
		}

		if (action === 'reject') {
			this.rejectionReason = 'Rejected by admin';
			this.postAcceptRejectCompanyApproval('reject');
			return;
		}

		if (action === 'block') {
			this.postAcceptRejectCompanyApproval('block');
		}
	}


	sortBy(field: string): void {

		if (this.sortField === field) {
			this.sortDirection =
				this.sortDirection === 'asc' ? 'desc' : 'asc';
		}
		else {
			this.sortField = field;
			this.sortDirection = 'asc';
		}

		this.companiesData.sort((a: any, b: any) => {

			let valueA = a[field];
			let valueB = b[field];

			if (valueA == null) valueA = '';
			if (valueB == null) valueB = '';

			if (typeof valueA === 'string') {
				valueA = valueA.toLowerCase();
				valueB = valueB.toLowerCase();
			}

			if (valueA > valueB) {
				return this.sortDirection === 'asc' ? 1 : -1;
			}

			if (valueA < valueB) {
				return this.sortDirection === 'asc' ? -1 : 1;
			}

			return 0;
		});
	}


	goToCompanyApprovalDetailPage(companyId: string) {
		this.router.navigate(['admin-student-platform/company-management/approval-detail-page', companyId], { queryParams: { origin: 'all-companies', page: this.page } });
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
		this.searchSubscription.unsubscribe();
	}

}
